<script setup lang="ts">
import { computed } from 'vue'
import { TicketIcon } from '@heroicons/vue/24/outline'
import { highlight } from '@/utils/highlight'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import type { OsTicketResult } from '@/api/omnisearchApi'

const props = defineProps<{
  item: OsTicketResult
  query: string
}>()

const emit = defineEmits<{ navigate: [] }>()

const permissionStore = useBoostCorePermissionStore()

// Les admins tickets gardent la vue admin ; les autres ouvrent la consultation.
const isTicketAdmin = computed(
  () => permissionStore.include('TICKETDEV_ADMIN') || permissionStore.include('SUPER_ADMIN'),
)

function handleClick() {
  emit('navigate')
  window.location.href = isTicketAdmin.value
    ? `/admin/tickets/${props.item.uuid}`
    : `/clients/tickets/${props.item.uuid}`
}

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'badge-error',
  IN_PROGRESS: 'badge-warning',
  RESOLVED: 'badge-success',
  CLOSED: 'badge-ghost',
}
</script>

<template>
  <button
    class="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-base-200 transition-colors text-left group cursor-pointer"
    @click="handleClick"
  >
    <span class="mt-0.5 flex-shrink-0 w-8 h-8 rounded-md bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
      <TicketIcon class="w-4 h-4 text-red-600 dark:text-red-400" />
    </span>
    <div class="flex-1 min-w-0">
      <p class="text-sm font-medium truncate text-base-content" v-html="highlight(item.title, query)" />
      <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
        <span v-if="item.internalReference" class="text-xs text-base-content/60">
          #<span v-html="highlight(String(item.internalReference), query)" />
        </span>
        <span
          v-if="item.description"
          class="text-xs text-base-content/50 truncate max-w-xs"
          v-html="highlight(item.description, query)"
        />
      </div>
    </div>
    <span :class="['badge badge-xs self-center flex-shrink-0', STATUS_COLORS[item.status] ?? 'badge-ghost']">
      {{ item.status }}
    </span>
  </button>
</template>
